import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Feature } from 'ol';
import React, {
  createContext,
  Dispatch,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DrawGeometryType } from '../isDrawGeometryType';
import { shapeToFeature, useOpenLayersContext } from '../../index';
import { zoomToFeatureExtent } from '../zoomToFeatureExtent';
import { featureToShape } from '../featureToShape';
import Map from 'ol/Map';
import { DrawingStyles } from '../styles/getDrawingStyle';

export type GeometryValidation = {
  isValid: boolean;
  reason: string;
  warning?: string;
};

interface DrawingContextProps {
  geometryType: DrawGeometryType | null;
  setGeometryType: (geometryType: DrawGeometryType | null) => void;
  drawnFeature: MutableRefObject<Feature | null>;
  existingFeature: MutableRefObject<Feature | null>;
  shape?: GeoJson | null;
  setShape: (shape: GeoJson | undefined | null) => void;
  showDrawPanel: boolean;
  setShowDrawPanel: (showDrawPanel: boolean) => void;
  onAccept: MutableRefObject<(() => GeoJson | null | void) | null>;
  onFinish: MutableRefObject<(() => void) | null>;
  onCancel: MutableRefObject<(() => void) | null>;
  onClear: MutableRefObject<(() => void) | null>;
  onDraw: MutableRefObject<((feature: Feature) => void) | null>;
  onUndo: MutableRefObject<(() => void) | null>;
  replaceFeature: MutableRefObject<((feature: Feature) => void) | null>;
  isValidDrawing: GeometryValidation;
  setIsValidDrawing: Dispatch<React.SetStateAction<GeometryValidation>>;
  undo: MutableRefObject<(() => void) | null>;
  clearDrawing: MutableRefObject<(() => void) | null>;
  finishDrawing: MutableRefObject<(() => void) | null>;
  drawingStyle: DrawingStyles;
  setDrawingStyle: Dispatch<React.SetStateAction<DrawingStyles>>;
  maxPoints: number | undefined;
  setMaxPoints: Dispatch<React.SetStateAction<number | undefined>>;
  isDrawing: boolean;
  radius: number | undefined;
  setRadius: Dispatch<React.SetStateAction<number | undefined>>;
  validateWhileDrawing: boolean;
  setValidateWhileDrawing: Dispatch<React.SetStateAction<boolean>>;
}

const DrawingContext = createContext<DrawingContextProps | undefined>(
  undefined
);

interface DrawingProviderProps {
  children: ReactNode;
}

export const DrawingProvider = ({ children }: DrawingProviderProps) => {
  const [geometryType, setGeometryType] = useState<DrawGeometryType | null>(
    null
  );

  const [isValidDrawing, setIsValidDrawing] = useState<GeometryValidation>({
    isValid: false,
    reason: 'No shape drawn.',
  });
  const isDrawing = useMemo(() => geometryType !== null, [geometryType]);

  // Important that we use refs here, as form-dialogs otherwise won't be able to see the changes
  const drawnFeature = useRef<Feature>(null);
  const existingFeature = useRef<Feature>(null);

  const [shape, setShape] = useState<GeoJson | undefined | null>(undefined);
  const [showDrawPanel, setShowDrawPanel] = useState<boolean>(false);
  const [drawingStyle, setDrawingStyle] = useState<DrawingStyles>(
    DrawingStyles.Normal
  );
  const [maxPoints, setMaxPoints] = useState<number | undefined>();
  const [radius, setRadius] = useState<number | undefined>();

  // Important that we use refs here, as form-dialogs otherwise won't be able to see the changes.
  //  These are calling the internal functions of useDrawings AND sets the states correctly in the DrawingProvider
  const onAccept = useRef(null);
  const onFinish = useRef(null);
  const onCancel = useRef(null);
  const onClear = useRef(null);
  const onDraw = useRef(null);
  const onUndo = useRef(null);

  // This controls validation while drawing specifically on linestrings. Using it in measure tool we need to validate while drawing
  //  whilst we do not want to do it when drawing a shape. If we do not validate on measure tool we are not able to get
  //  the measurements, and if we do it on drawing shape, the user will be able unintentionally draw line TO the
  //  finish button in the draw menu, which isn't intended. February 17th 2025 (nbs)
  const [validateWhileDrawing, setValidateWhileDrawing] = useState(false);

  // These are internal functions that's set by the useDrawings hook
  const undo = useRef(null);
  const clearDrawing = useRef(null);
  const finishDrawing = useRef(null);
  const replaceFeature = useRef(null);

  const contextValue = useMemo(
    () => ({
      geometryType,
      setGeometryType,
      drawnFeature,
      existingFeature,
      shape,
      setShape,
      showDrawPanel,
      setShowDrawPanel,
      onAccept,
      onFinish,
      onCancel,
      onClear,
      onDraw,
      onUndo,
      isValidDrawing,
      setIsValidDrawing,
      undo,
      clearDrawing,
      finishDrawing,
      replaceFeature,
      drawingStyle,
      setDrawingStyle,
      maxPoints,
      setMaxPoints,
      radius,
      setRadius,
      isDrawing,
      validateWhileDrawing,
      setValidateWhileDrawing,
    }),
    [
      geometryType,
      setGeometryType,
      drawnFeature,
      existingFeature,
      shape,
      setShape,
      showDrawPanel,
      setShowDrawPanel,
      onAccept,
      onFinish,
      onCancel,
      onClear,
      onDraw,
      onUndo,
      isValidDrawing,
      setIsValidDrawing,
      undo,
      clearDrawing,
      finishDrawing,
      replaceFeature,
      drawingStyle,
      setDrawingStyle,
      maxPoints,
      setMaxPoints,
      radius,
      setRadius,
      isDrawing,
      validateWhileDrawing,
      setValidateWhileDrawing,
    ]
  );

  return (
    <DrawingContext.Provider value={contextValue}>
      {children}
    </DrawingContext.Provider>
  );
};

export const useDrawingContext = () => {
  const context = useContext(DrawingContext);

  if (!context) {
    throw new Error('useDrawingContext must be used within a DrawingProvider');
  }
  return context;
};

export const useResetDrawingContext = () => {
  const drawingContext = useDrawingContext();

  const resetDrawingContext = useCallback(() => {
    drawingContext.setGeometryType(null);
    drawingContext.drawnFeature.current = null;
    drawingContext.existingFeature.current = null;
    drawingContext.setShowDrawPanel(false);
    drawingContext.setShape(undefined);
    drawingContext.setIsValidDrawing({
      isValid: false,
      reason: 'No shape drawn.',
    });
    drawingContext.setDrawingStyle(DrawingStyles.Normal);
    drawingContext.setMaxPoints(undefined);
    drawingContext.setRadius(undefined);
    drawingContext.setValidateWhileDrawing(false);
  }, [drawingContext]);

  return resetDrawingContext;
};

export const useDrawingLifecycleBase = (map: Map) => {
  const drawingContext = useDrawingContext();
  const resetDrawingContext = useResetDrawingContext();
  const { displayProjection, dataProjection } = useOpenLayersContext();

  const onAcceptDrawing = (): GeoJson | undefined => {
    const shape = featureToShape(
      drawingContext.drawnFeature.current,
      displayProjection,
      dataProjection
    );

    drawingContext.setShape(shape);
    drawingContext.setShowDrawPanel(false);
    drawingContext.setRadius(undefined);
    return shape;
  };

  const onCancelDrawing = () => {
    resetDrawingContext();
  };

  const onModifyDraw = useCallback(
    (feature: Feature) => {
      drawingContext.drawnFeature.current = feature;
    },
    [drawingContext.drawnFeature]
  );

  const onFinishDrawing = () => {
    if (drawingContext.finishDrawing.current) {
      drawingContext.finishDrawing.current();
    }
  };

  const onClearDrawing = () => {
    drawingContext.drawnFeature.current = null;
    drawingContext.existingFeature.current = null;
    drawingContext.setShape(undefined);
    drawingContext.setRadius(undefined);

    if (drawingContext.clearDrawing.current) {
      drawingContext.clearDrawing.current();
    }
  };

  const onUndoLastDraw = () => {
    if (drawingContext.undo.current) {
      drawingContext.undo.current();
    }
  };

  const onStartDrawing = (
    geometryType: DrawGeometryType,
    shape: GeoJson | undefined | null,
    showDrawPanel = false,
    drawingStyle?: DrawingStyles,
    maxPoints?: number
  ) => {
    resetDrawingContext();
    const feature = shapeToFeature(shape, displayProjection, dataProjection);

    drawingContext.setDrawingStyle(drawingStyle ?? DrawingStyles.Normal);

    // Default to the standard onAcceptingDrawing function, if not provided by the caller
    if (!drawingContext.onAccept.current) {
      drawingContext.onAccept.current = onAcceptDrawing;
    }

    // Default to the standard onAcceptingDrawing function, if not provided by the caller
    if (!drawingContext.onFinish.current) {
      drawingContext.onFinish.current = onFinishDrawing;
    }

    // Default to the standard onCancelDrawing function, if not provided by the caller
    if (!drawingContext.onCancel.current) {
      drawingContext.onCancel.current = onCancelDrawing;
    }

    // Default to the standard onClearDrawing function, if not provided by the caller
    if (!drawingContext.onClear.current) {
      drawingContext.onClear.current = onClearDrawing;
    }

    // Default to the standard onDraw function, if not provided by the caller
    if (!drawingContext.onDraw.current) {
      drawingContext.onDraw.current = onModifyDraw;
    }

    // Default to the standard onUndo function, if not provided by the caller
    if (!drawingContext.onUndo.current) {
      drawingContext.onUndo.current = onUndoLastDraw;
    }

    drawingContext.setShowDrawPanel(showDrawPanel);
    drawingContext.setGeometryType(geometryType);
    drawingContext.existingFeature.current = feature;
    drawingContext.drawnFeature.current = feature;
    drawingContext.setShape(shape);
    drawingContext.setMaxPoints(maxPoints);

    if (feature) {
      zoomToFeatureExtent(feature, map);
    }
  };

  return {
    onStartDrawing,
    onAcceptDrawing,
    onFinishDrawing,
    onCancelDrawing,
    onClearDrawing,
    onModifyDraw,
  };
};

export const useDrawingLifecycle = () => {
  const { map } = useOpenLayersContext();

  return useDrawingLifecycleBase(map);
};
